import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { fetchCurrentUser, loginWithPassword, logoutCurrentUser } from "@/lib/authApi";

export interface AuthOrganization {
  code: string;
}

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  fullName: string;
  avatarUrl?: string | null;
  isActive: boolean;
  isSuperuser: boolean;
  isTeacherAdmin: boolean;
  organizations: Array<{
    organizationId: string;
    organizationName: string;
    organizationCode: string;
    role: "student" | "teacher_admin";
    status: string;
    studentNumber?: string | null;
  }>;
  loginCount?: number;
  lastLoginAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
  organization: AuthOrganization | null;
}

export type AuthStatus = "checking" | "authenticated" | "guest";

type LoginParams = {
  identifier: string;
  password: string;
  rememberMe: boolean;
};

interface AuthContextType {
  user: AuthUser | null;
  status: AuthStatus;
  signIn: (params: LoginParams) => Promise<{ ok: boolean; message?: string; user?: AuthUser }>;
  signOut: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>("checking");

  const refreshSession = useCallback(async () => {
    setStatus(previous => (previous === "authenticated" ? previous : "checking"));

    try {
      const nextUser = await fetchCurrentUser();
      if (nextUser) {
        setUser(nextUser);
        setStatus("authenticated");
        return;
      }
    } catch (error) {
      console.error("Fetch current user failed:", error);
    }

    setUser(null);
    setStatus("guest");
  }, []);

  useEffect(() => {
    refreshSession().catch(error => {
      console.error("Initial auth bootstrap failed:", error);
      setUser(null);
      setStatus("guest");
    });
  }, [refreshSession]);

  const signIn = useCallback(async ({ identifier, password, rememberMe }: LoginParams) => {
    const result = await loginWithPassword({
      identifier: identifier.trim(),
      password,
      rememberMe,
    });

    if (!result.ok || !result.user) {
      setUser(null);
      setStatus("guest");
      return {
        ok: false,
        message: result.message,
      };
    }

    setUser(result.user);
    setStatus("authenticated");
    return { ok: true, user: result.user };
  }, []);

  const signOut = useCallback(async () => {
    try {
      await logoutCurrentUser();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      setStatus("guest");
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      status,
      signIn,
      signOut,
      refreshSession,
    }),
    [refreshSession, signIn, signOut, status, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
